// Sessão Live do NEXU — áudio bidirecional em tempo real com a Live API do Gemini (via proxy WebSocket).
// Captura o microfone em PCM16 16kHz, envia pelo WS, recebe áudio 24kHz e toca em streaming com buffer.

export class NexuLiveSession {
  constructor({ onStatus, onTranscriptIn, onTranscriptOut, onText, onSpeakingChange, onError, onUi, onTurnStart, onTurnComplete, onExecuting, onNavigate, onVolume, onFilterPanel, onActionPanel, onActionProgress, onActionDone, onMediaPanel, onGamesPanel, onMessagePanel, onFlowStep, onListPanel, onClosePanel, onFinancePanel, onInputPanel, onPreparing }) {
    this.onStatus = onStatus || (() => {});
    this.onTranscriptIn = onTranscriptIn || (() => {});
    this.onTranscriptOut = onTranscriptOut || (() => {});
    this.onText = onText || (() => {});
    this.onSpeakingChange = onSpeakingChange || (() => {});
    this.onUi = onUi || (() => {});
    this.onTurnStart = onTurnStart || (() => {});
    this.onTurnComplete = onTurnComplete || (() => {});
    this.onExecuting = onExecuting || (() => {});
    this.onNavigate = onNavigate || (() => {});
    this.onVolume = onVolume || (() => {});
    this.onFilterPanel = onFilterPanel || (() => {});
    this.onActionPanel = onActionPanel || (() => {});
    this.onActionProgress = onActionProgress || (() => {});
    this.onActionDone = onActionDone || (() => {});
    this.onMediaPanel = onMediaPanel || (() => {});
    this.onGamesPanel = onGamesPanel || (() => {});
    this.onMessagePanel = onMessagePanel || (() => {});
    this.onFlowStep = onFlowStep || (() => {});
    this.onListPanel = onListPanel || (() => {});
    this.onInputPanel = onInputPanel || (() => {});
    this.onPreparing = onPreparing || (() => {});
    this.onClosePanel = onClosePanel || (() => {});
    this.onFinancePanel = onFinancePanel || (() => {});
    this._turnActive = false;
    this.onError = onError || (() => {});
    this.ws = null;
    this.audioCtxIn = null;   // captura (16kHz)
    this.audioCtxOut = null;  // reprodução (24kHz)
    this.micStream = null;
    this.processor = null;
    this.source = null;
    this.playQueue = [];      // sources agendados (para parar no barge-in)
    this.nextPlayTime = 0;
    this._audioFila = [];     // chunks Float32 decodificados aguardando reprodução (jitter buffer)
    this._tocando = false;    // se o agendador está ativo
    this._primeiroChunk = true;
    this.playing = false;
    this.closed = false;
    this.gainNode = null;
    try { this.volume = parseFloat(localStorage.getItem('nexu_volume')); if (isNaN(this.volume)) this.volume = 1.0; } catch { this.volume = 1.0; }
  }

  // destrava o áudio do iOS — chamar DENTRO de um gesto do usuário (toque/clique)
  static unlockAudio() {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!window.__nexuUnlockCtx) window.__nexuUnlockCtx = new Ctx();
      const ctx = window.__nexuUnlockCtx;
      if (ctx.state === 'suspended') ctx.resume();
      // toca um buffer silencioso para "acordar" o áudio
      const b = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = b; src.connect(ctx.destination); src.start(0);
    } catch {}
  }

  async start() {
    const token = localStorage.getItem('nexus-token');
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = base.replace(/^http/, 'ws') + `/api/nexu/live?token=${encodeURIComponent(token)}`;

    // microfone
    this.micStream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });

    this.ws = new WebSocket(wsUrl);
    this.ws.binaryType = 'arraybuffer';

    // timeout: se a conexão não abrir em 8s, considera falha (o overlay cai no fallback)
    let _opened = false;
    const _connectTimeout = setTimeout(() => {
      if (!_opened && !this.closed) {
        try { this.ws && this.ws.close(); } catch {}
        this.onError('timeout de conexão');
      }
    }, 8000);

    this.ws.onopen = () => { _opened = true; clearTimeout(_connectTimeout); this.onStatus('conectando'); };
    this.ws.onerror = () => { clearTimeout(_connectTimeout); this.onError('erro de conexão'); };
    this.ws.onclose = () => { clearTimeout(_connectTimeout); if (!this.closed) this.onStatus('desconectado'); };

    this.ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.type === 'preparing') { this.onPreparing(msg.message || 'Preparando…'); }
      else if (msg.type === 'ready') { this.onStatus('pronto'); this._startCapture(); }
      else if (msg.type === 'audio') { if (!this._turnActive) { this._turnActive = true; this.onTurnStart(); } this._enqueueAudio(msg.data); }
      else if (msg.type === 'transcript_in') { this.onTranscriptIn(msg.text); }
      else if (msg.type === 'transcript_out') { if (!this._turnActive) { this._turnActive = true; this.onTurnStart(); } this.onTranscriptOut(msg.text); }
      else if (msg.type === 'text') { this.onText(msg.text); }
      else if (msg.type === 'ui') { this.onUi(msg.ui); }
      else if (msg.type === 'executing') { this.onExecuting(msg.tool); }
      else if (msg.type === 'navigate') { this.onNavigate(msg.to); }
      else if (msg.type === 'volume') { this.onVolume(msg); }
      else if (msg.type === 'filter_panel') { this.onFilterPanel(msg.data); }
      else if (msg.type === 'media_panel') { this.onMediaPanel(msg.data); }
      else if (msg.type === 'games_panel') { this.onGamesPanel(msg.data); }
      else if (msg.type === 'list_panel') { this.onListPanel(msg.data); }
      else if (msg.type === 'input_panel') { this.onInputPanel(msg.data); }
      else if (msg.type === 'close_panel') { this.onClosePanel(msg.data); }
      else if (msg.type === 'finance_panel') { this.onFinancePanel(msg.data); }
      else if (msg.type === 'flow_step') { this.onFlowStep(msg.data); }
      else if (msg.type === 'message_panel') { this.onMessagePanel(msg.data); }
      else if (msg.type === 'data_changed') { try { window.dispatchEvent(new CustomEvent('nexu-data-changed')); localStorage.setItem('nexu_data_changed', String(Date.now())); } catch {} }
      else if (msg.type === 'action_panel') { this.onActionPanel(msg); }
      else if (msg.type === 'action_progress') { this.onActionProgress(msg); }
      else if (msg.type === 'action_done') { this.onActionDone(msg); }
      else if (msg.type === 'interrupted') { this._clearPlayback(); }
      else if (msg.type === 'turn_complete') { this._turnActive = false; this.onTurnComplete(); }
      else if (msg.type === 'error') { this.onError(msg.message || 'erro'); }
    };
  }

  // ---- captura do microfone → PCM16 16kHz → WS (compatível com iOS/Safari) ----
  _startCapture() {
    // iOS NÃO deixa forçar 16000; usa o sampleRate nativo e reamostramos manualmente.
    this.audioCtxIn = new (window.AudioContext || window.webkitAudioContext)();
    if (this.audioCtxIn.state === 'suspended') this.audioCtxIn.resume().catch(() => {});
    const inRate = this.audioCtxIn.sampleRate;  // ex.: 48000
    this.source = this.audioCtxIn.createMediaStreamSource(this.micStream);
    this.processor = this.audioCtxIn.createScriptProcessor(4096, 1, 1);
    this.processor.onaudioprocess = (e) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);
      // reamostra de inRate para 16000
      const ratio = inRate / 16000;
      const outLen = Math.floor(input.length / ratio);
      const pcm = new Int16Array(outLen);
      for (let i = 0; i < outLen; i++) {
        const s = Math.max(-1, Math.min(1, input[Math.floor(i * ratio)]));
        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
      }
      this.ws.send(pcm.buffer);
    };
    this.source.connect(this.processor);
    this.processor.connect(this.audioCtxIn.destination);
    this.onStatus('ouvindo');
  }

  // ---- reprodução em streaming (áudio 24kHz do Gemini) — compatível iOS ----
  _enqueueAudio(b64) {
    // AudioContext de saída FIXO em 24000Hz (taxa do Gemini)
    if (!this.audioCtxOut) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      try { this.audioCtxOut = new Ctx({ sampleRate: 24000 }); }
      catch { this.audioCtxOut = new Ctx(); }
    }
    const ctx = this.audioCtxOut;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const ctxRate = ctx.sampleRate;
    // decode base64 PCM16 -> Float32
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const pcm = new Int16Array(bytes.buffer);
    let f32 = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) f32[i] = pcm[i] / 32768;
    if (ctxRate !== 24000) {
      const ratio = ctxRate / 24000;
      const nl = Math.floor(f32.length * ratio);
      const res = new Float32Array(nl);
      for (let i = 0; i < nl; i++) {
        const pos = i / ratio, i0 = Math.floor(pos), frac = pos - i0;
        res[i] = (f32[i0] || 0) * (1 - frac) + (f32[i0 + 1] || f32[i0] || 0) * frac;
      }
      f32 = res;
    }
    // coloca na fila do jitter buffer e dispara o agendador
    clearTimeout(this._fimTimer);
    this._audioFila.push(f32);
    this._agendarReproducao();
  }

  // JITTER BUFFER: acumula chunks e toca em sequência contínua. Se a fila esvazia,
  // PAUSA e espera reacumular (evita o "trava e recomeça" no mobile com rede oscilando).
  _agendarReproducao() {
    const ctx = this.audioCtxOut;
    if (!ctx) return;
    const isMob = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const preBuffer = isMob ? 0.45 : 0.15;   // acumula antes de começar
    const ctxRate = ctx.sampleRate;

    // duração total acumulada na fila (em segundos)
    const durFila = this._audioFila.reduce((a, f) => a + f.length / ctxRate, 0);

    // se não está tocando, só começa quando tiver buffer suficiente acumulado
    if (!this._tocando) {
      if (durFila < preBuffer) return;       // espera acumular mais
      this._tocando = true;
      this._primeiroChunk = false;
      this.nextPlayTime = ctx.currentTime + 0.05;
      this.onSpeakingChange(true);
    }

    if (!this.gainNode) {
      this.gainNode = ctx.createGain();
      this.gainNode.gain.value = this.volume;
      this.gainNode.connect(ctx.destination);
    }

    // agenda TODOS os chunks disponíveis na fila, emendados
    while (this._audioFila.length) {
      const f32 = this._audioFila.shift();
      const buffer = ctx.createBuffer(1, f32.length, ctxRate);
      buffer.getChannelData(0).set(f32);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(this.gainNode);
      const now = ctx.currentTime;
      if (this.nextPlayTime < now) this.nextPlayTime = now + 0.02;
      src.start(this.nextPlayTime);
      this.nextPlayTime += buffer.duration;
      this.playQueue.push(src);
      src.onended = () => {
        this.playQueue = this.playQueue.filter((s) => s !== src);
        // fila vazia E nada agendado → confirma fim após 150ms (evita cortar se vier mais áudio)
        if (this.playQueue.length === 0 && this._audioFila.length === 0) {
          clearTimeout(this._fimTimer);
          this._fimTimer = setTimeout(() => {
            if (this.playQueue.length === 0 && this._audioFila.length === 0) {
              this._tocando = false;
              this.onSpeakingChange(false);
            }
          }, 150);
        }
      };
    }
  }

  _clearPlayback() {
    // corta a fala (barge-in / interrupção)
    this.playQueue.forEach((s) => { try { s.stop(); } catch {} });
    this.playQueue = [];
    this._audioFila = [];
    this._tocando = false;
    this._primeiroChunk = true;
    this.nextPlayTime = 0;
    this.onSpeakingChange(false);
  }

  setVolume(v) {
    this.volume = Math.max(0, Math.min(2, v));
    if (this.gainNode) this.gainNode.gain.value = this.volume;
    try { localStorage.setItem('nexu_volume', String(this.volume)); } catch {}
  }

  // enviar texto digitado
  sendText(text) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'text', text }));
    }
  }

  // interromper a fala do NEXU (o usuário quer falar)
  interrupt() { this._clearPlayback(); }

  stop() {
    this.closed = true;
    try { this.processor && this.processor.disconnect(); } catch {}
    try { this.source && this.source.disconnect(); } catch {}
    try { this.micStream && this.micStream.getTracks().forEach((t) => t.stop()); } catch {}
    try { this.audioCtxIn && this.audioCtxIn.close(); } catch {}
    this._clearPlayback();
    try { this.audioCtxOut && this.audioCtxOut.close(); } catch {}
    try { this.ws && this.ws.close(); } catch {}
  }
}
